/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useRef, useMemo, useContext, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import Layers from '@mui/icons-material/Layers';
import StyledProps from '../theme/AppThemeInterface';
import styled from 'styled-components';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import { selectOverlaysPanelShown } from '../../../store';
import { Tooltip } from '@mui/material';

interface OverlaysMapControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
}

export function OverlaysMapControl({ theme, map, isFullScreen }: OverlaysMapControlProps) {
  const dispatch = useDispatch();
  const overlaysPanelIcon = useRef<HTMLElement>();
  const mapActionsContext = useContext(MapActionsContext);
  const overlaysPanelShown = useSelectorWithMapId(selectOverlaysPanelShown);
  const { toggleOverlaysPanel } = mapActionsContext.mapActions;

  useLayoutEffect(() => {
    if (overlaysPanelIcon.current && overlaysPanelIcon.current.classList) {
      if (overlaysPanelShown) {
        overlaysPanelIcon.current.classList.add('overlays_panel_icon--active');
      } else {
        overlaysPanelIcon.current.classList.remove('overlays_panel_icon--active');
      }
    }
  }, [overlaysPanelShown]);

  const items = useMemo(
    () => [
      {
        render: () => (
          <Tooltip
            arrow
            title="Map Overlays"
            placement="left-start"
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: theme.tooltipBackgroundColor },
              },
              arrow: {
                sx: { color: theme.tooltipBackgroundColor },
              },
            }}
          >
            <StyledLayers id="overlays_panel_btn" ref={overlaysPanelIcon} />
          </Tooltip>
        ),
        buttonTextColor: theme.errorTextColor,
        id: 'overlays_panel_btn_container',
        onClick: () => {
          dispatch(toggleOverlaysPanel());
        },
      },
    ],
    [dispatch, overlaysPanelIcon, theme, toggleOverlaysPanel],
  );

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      hidden={isFullScreen}
      index={MapControlOrderIndex.OVERLAYS_CONTROL}
    />
  );
}

export default withTheme(OverlaysMapControl);

const StyledLayers = styled(Layers)`
  width: 20px !important;
`;
